import { useCallback, useRef, useState } from "react";
import { getFaviconUrl } from "@/lib/favicon";

/**
 * Resolves a site favicon and drops it once it is known to be unusable, so
 * callers can draw their own glyph instead of a broken image. Plenty of sites
 * answer `/favicon.ico` with a 403 or 404, and `onError` is not enough to catch
 * it: an image the browser starts loading from server-rendered HTML usually
 * fails before React attaches a handler, and that event is never replayed.
 * `decode()` settles on the image's final state instead of relying on an event
 * firing at the right moment.
 */
export function useFavicon(url?: string) {
  const resolved = url ? getFaviconUrl(url) : null;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = resolved && resolved !== failedSrc ? resolved : null;
  // Welche Quelle gerade am Knoten hängt. Die Vorlage merkt sich das über eine
  // Aufräumfunktion, die der Ref-Callback zurückgibt — das versteht erst React
  // 19; React 18 warnt und ruft sie nie auf. Ein Merker leistet dasselbe: Eine
  // späte Absage zählt nur, wenn sie das Bild betrifft, das noch angezeigt wird.
  const attachedSrc = useRef<string | null>(null);

  const ref = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img || !src) {
        attachedSrc.current = null;
        return;
      }

      attachedSrc.current = src;

      // `decode()` fehlt in älteren Umgebungen und in jsdom; ohne die Prüfung
      // fliegt hier eine Ausnahme aus dem Ref-Callback heraus.
      const decoded = img.decode?.();
      if (!decoded) return;

      decoded.catch(() => {
        if (attachedSrc.current === src) setFailedSrc(src);
      });
    },
    [src],
  );

  return { src, ref };
}
